// src/modules/tenant/ProductFormPage.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_ROOT_URL } from "../../config/api";
import initialMockData from "./mockProducts.json";

const THEME_PRIMARY = "var(--color-primary, #059669)";
const THEME_PRIMARY_SOFT = "color-mix(in srgb, var(--color-primary, #059669) 12%, white)";
const THEME_PRIMARY_RING = "color-mix(in srgb, var(--color-primary, #059669) 45%, transparent)";

const authenticatedJsonHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
});

const ProductFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const isEditMode = Boolean(id);

    const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
    const tenantId = loggedInUser.tenantId;
    const userId = loggedInUser.id;

    const returnPath = location.state?.returnPath || "/tenant/dashboard?tab=products-only";

    const [saving, setSaving] = useState(false);
    const [productTagsInput, setProductTagsInput] = useState("");
    const [imagePreview, setImagePreview] = useState("");
    const imageInputRef = useRef<HTMLInputElement>(null);

    // Autocomplete states
    const [showEventDropdown, setShowEventDropdown] = useState(false);
    const [eventSearch, setEventSearch] = useState("");
    const [showAssetDropdown, setShowAssetDropdown] = useState(false);
    const [assetSearch, setAssetSearch] = useState("");

    // Available Options (from Mock JSON)
    const [availableEvents, setAvailableEvents] = useState<any[]>([]);
    const [availableAssets, setAvailableAssets] = useState<any[]>([]);
    const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);

    // Form State
    const [productFormData, setProductFormData] = useState<any>({
        name: "",
        description: "",
        launchDate: "",
        endDate: "",
        notes: "",
        tags: [],
        status: "Active",
        image: "",
        video: "",
        events: [],
        assets: [],
    });

    // --- MOCK DATA MANAGEMENT ---
    const getMockData = () => {
        const data = localStorage.getItem("mockProducts");
        if (!data) {
            localStorage.setItem("mockProducts", JSON.stringify(initialMockData));
            return initialMockData;
        }
        return JSON.parse(data);
    };

    // Fetch existing product if in Edit mode + load available options
    useEffect(() => {
        let cancelled = false;

        // Keep existing event and asset dropdown options for now.
        setAvailableEvents(initialMockData.availableEvents || []);
        setAvailableAssets([]);

        const loadProduct = async () => {
            if (!isEditMode || !id) return;

            try {
                const response = await fetch(
                    `${API_ROOT_URL}/products/${encodeURIComponent(id)}`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(
                        result.message || "Failed to load product."
                    );
                }

                const product = result.data;

                if (!product || !product.product_id) {
                    throw new Error("Product not found.");
                }

                if (cancelled) return;

                const mappedProduct = {
                    id: String(product.product_id),
                    name: product.title || "",
                    description: product.description || "",
                    notes: product.notes || "",
                    launchDate: product.start_date
                        ? product.start_date.split("T")[0]
                        : "",
                    endDate: product.end_date
                        ? product.end_date.split("T")[0]
                        : "",
                    tags: Array.isArray(product.tags) ? product.tags : [],
                    status: product.status || "Active",
                    image: product.image_url || "",
                    video: product.video_url || "",
                    events: [],
                    assets: [],
                };

                setProductFormData(mappedProduct);
                setProductTagsInput(mappedProduct.tags.join(", "));
                setImagePreview(mappedProduct.image);
                // Load previously linked assets
                const assetResponse = await fetch(
                    `${API_ROOT_URL}/product-assets/${encodeURIComponent(id!)}`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const assetResult = await assetResponse.json();

                if (!assetResponse.ok || assetResult.success === false) {
                    throw new Error(assetResult.message || "Failed to load linked assets.");
                }

                const linkedAssets = Array.isArray(assetResult.data)
                    ? assetResult.data
                    : [];

                setProductFormData((prev: any) => ({
                    ...prev,
                    assets: linkedAssets.map((asset: any) => ({
                        id: String(asset.asset_id ?? asset.id),
                        name: asset.original_filename || asset.filename || asset.name || "Untitled Asset",
                    })),
                }));

                // Load previously linked events
                const eventResponse = await fetch(
                    `${API_ROOT_URL.replace(/\/api\/?$/, "")}/events/products/${encodeURIComponent(id!)}/events`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const eventResult = await eventResponse.json();

                if (!eventResponse.ok || eventResult.success === false) {
                    throw new Error(eventResult.message || "Failed to load linked events.");
                }

                const linkedEvents = Array.isArray(eventResult.data)
                    ? eventResult.data
                    : [];

                setProductFormData((prev: any) => ({
                    ...prev,
                    events: linkedEvents.map((event: any) => ({
                        id: String(event.event_id),
                        name: event.title || "Untitled Event",
                    })),
                }));
            } catch (error: any) {
                if (!cancelled) {
                    console.error("Failed to load product:", error);
                    toast.error(error.message || "Unable to load product.");
                }
            }
        };

        loadProduct();

        return () => {
            cancelled = true;
        };
    }, [isEditMode, id]);


    // Fetch real events from the backend
    useEffect(() => {
        const fetchAvailableEvents = async () => {
            try {
                if (!tenantId) {
                    toast.error("Tenant ID is missing. Please log in again.");
                    return;
                }

                const response = await fetch(
                    `${API_ROOT_URL.replace(/\/api\/?$/, "")}/events?tenant_id=${tenantId}&page=1&limit=100`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(result.message || "Failed to fetch events.");
                }

                const events = Array.isArray(result.data) ? result.data : [];

                // Convert backend event fields into the format used by the UI
                const formattedEvents = events
                    .filter((event: any) =>
                        String(event.status || "").toUpperCase() !== "INACTIVE"
                    )
                    .map((event: any) => ({
                        id: String(event.event_id),
                        name: event.title || "Untitled Event",
                        status: event.status,
                        startDate: event.start_date,
                        endDate: event.end_date,
                    }));

                setAvailableEvents(formattedEvents);
            } catch (error: any) {
                console.error("Fetch events error:", error);
                toast.error(error.message || "Unable to load events.");
            }
        };

        fetchAvailableEvents();
    }, [tenantId]);


    // Fetch real assets from the backend
    useEffect(() => {
        let cancelled = false;

        const fetchAvailableAssets = async () => {
            try {
                if (!tenantId) {
                    toast.error("Tenant ID is missing. Please log in again.");
                    return;
                }

                const response = await fetch(
                    `${API_ROOT_URL}/assets?tenant_id=${encodeURIComponent(tenantId)}&limit=200&skip_preview=true`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const result = await response.json();

                if (!response.ok || result.success === false) {
                    throw new Error(result.message || "Failed to fetch assets.");
                }

                const assets = Array.isArray(result.data)
                    ? result.data
                    : Array.isArray(result)
                        ? result
                        : [];

                const formattedAssets = assets.map((asset: any) => ({
                    id: String(asset.asset_id ?? asset.id),
                    name:
                        asset.original_filename ||
                        asset.filename ||
                        asset.name ||
                        "Untitled Asset",
                    status: asset.status,
                    container: asset.container,
                }));

                if (!cancelled) {
                    setAvailableAssets(formattedAssets);
                }
            } catch (error: any) {
                console.error("Fetch assets error:", error);

                if (!cancelled) {
                    setAvailableAssets([]);
                    toast.error(error.message || "Unable to load assets.");
                }
            }
        };

        fetchAvailableAssets();

        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    // Click outside handler to close dropdowns
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.event-dropdown-container')) {
                setShowEventDropdown(false);
            }
            if (!target.closest('.asset-dropdown-container')) {
                setShowAssetDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSave = async () => {
        if (!productFormData.name?.trim()) {
            toast.warning("Please enter a product name.");
            return;
        }

        if (
            productFormData.launchDate &&
            productFormData.endDate &&
            productFormData.endDate < productFormData.launchDate
        ) {
            toast.warning("End date cannot be before the launch date.");
            return;
        }

        if (!tenantId || !userId) {
            toast.error("User or tenant information is missing. Please log in again.");
            return;
        }

        setSaving(true);

        try {
            const payload = {
                title: productFormData.name.trim(),
                description: productFormData.description || "",
                notes: productFormData.notes || "",
                start_date: productFormData.launchDate
                    ? new Date(productFormData.launchDate).toISOString()
                    : new Date().toISOString(),
                end_date: productFormData.endDate
                    ? new Date(productFormData.endDate).toISOString()
                    : null,
                status: productFormData.status || "Active",
                tags: productFormData.tags || [],
                tenant_id: tenantId,
                updated_by: userId,
                ...(!isEditMode && { created_by: userId }),
            };

            const url = isEditMode
                ? `${API_ROOT_URL}/products/${encodeURIComponent(id!)}`
                : `${API_ROOT_URL}/products`;

            const response = await fetch(url, {
                method: isEditMode ? "PUT" : "POST",
                headers: authenticatedJsonHeaders(),
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                    `Failed to ${isEditMode ? "update" : "create"} product.`
                );
            }

            // Get the product ID from the API response.
            // In edit mode, use the existing product ID.
            const savedProductId = isEditMode
                ? id
                : result.data?.product_id || result.product_id;

            if (!savedProductId) {
                throw new Error("Product saved, but product ID was not returned.");
            }

            // Save linked assets
            const assetResponse = await fetch(
                `${API_ROOT_URL}/product-assets/${encodeURIComponent(savedProductId)}`,
                {
                    method: "PUT",
                    headers: authenticatedJsonHeaders(),
                    body: JSON.stringify({
                        asset_ids: [
                            ...new Set([
                                ...productFormData.assets.map((asset: any) => asset.id),
                                ...selectedAssetIds,
                            ]),
                        ],
                        updated_by: userId,
                    }),
                }
            );

            const assetResult = await assetResponse.json();

            if (!assetResponse.ok || assetResult.success === false) {
                throw new Error(
                    assetResult.message || "Product saved, but linking assets failed."
                );
            }

            // Save linked events
            const eventResponse = await fetch(
                `${API_ROOT_URL.replace(/\/api\/?$/, "")}/events/products/${encodeURIComponent(savedProductId)}/events`,
                {
                    method: "PUT",
                    headers: authenticatedJsonHeaders(),
                    body: JSON.stringify({
                        event_ids: [
                            ...new Set([
                                ...productFormData.events.map((event: any) => event.id),
                                ...selectedEventIds,
                            ]),
                        ],
                    }),
                }
            );

            const eventResult = await eventResponse.json();

            if (!eventResponse.ok || eventResult.success === false) {
                throw new Error(
                    eventResult.message || "Product saved, but linking events failed."
                );
            }

            toast.success(
                `Product ${isEditMode ? "updated" : "created"} successfully!`
            );

            setTimeout(() => {
                navigate(returnPath);
            }, 800);

        } catch (error: any) {
            console.error("Product save failed:", error);
            toast.error(error.message || "An error occurred while saving the product.");
        } finally {
            setSaving(false);
        }
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result as string;
                setImagePreview(base64);
                setProductFormData((prev: any) => ({ ...prev, image: base64 }));
            };
            reader.readAsDataURL(file);
        }
    };

    // --- LINKING HANDLERS ---
    const toggleSelectEvent = (id: string) => {
        setSelectedEventIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAsset = (id: string) => {
        setSelectedAssetIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleBulkAddEvents = () => {
        const toAdd = availableEvents.filter(e => selectedEventIds.includes(e.id));
        const newEvents = [...productFormData.events];
        toAdd.forEach(evt => {
            if (!newEvents.find((e: any) => e.id === evt.id)) newEvents.push(evt);
        });
        setProductFormData((prev: any) => ({ ...prev, events: newEvents }));
        setSelectedEventIds([]);
        setShowEventDropdown(false);
        setEventSearch("");
    };

    const handleBulkAddAssets = () => {
        const toAdd = availableAssets.filter(a => selectedAssetIds.includes(a.id));
        const newAssets = [...productFormData.assets];
        toAdd.forEach(ast => {
            if (!newAssets.find((a: any) => a.id === ast.id)) newAssets.push(ast);
        });
        setProductFormData((prev: any) => ({ ...prev, assets: newAssets }));
        setSelectedAssetIds([]);
        setShowAssetDropdown(false);
        setAssetSearch("");
    };

    const removeEvent = (id: string) => {
        setProductFormData((prev: any) => ({ ...prev, events: prev.events.filter((e: any) => e.id !== id) }));
    };

    const removeAsset = (id: string) => {
        setProductFormData((prev: any) => ({ ...prev, assets: prev.assets.filter((a: any) => a.id !== id) }));
    };

    // Filtered lists for autocomplete
    const filteredEvents = availableEvents.filter(e =>
        e.name.toLowerCase().includes(eventSearch.toLowerCase()) &&
        !productFormData.events.find((added: any) => added.id === e.id)
    );

    const filteredAssets = availableAssets.filter(a =>
        a.name.toLowerCase().includes(assetSearch.toLowerCase()) &&
        !productFormData.assets.find((added: any) => added.id === a.id)
    );

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-4">
            <ToastContainer position="top-right" autoClose={3000} theme="colored" />

            {/* Breadcrumbs */}
            <nav className="flex items-center gap-1.5 text-xs text-gray-500">
                <button onClick={() => navigate("/tenant/dashboard")} className="hover:text-purple-600 transition-colors">
                    Dashboard
                </button>
                <span>/</span>
                <button onClick={() => navigate("/tenant/dashboard?tab=products-only")} className="hover:text-purple-600 transition-colors">
                    Products
                </button>
                <span>/</span>
                <span className="font-medium text-gray-800">
                    {isEditMode ? "Edit Product" : "Create Product"}
                </span>
            </nav>

            {/* Page Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">
                    {isEditMode ? "Edit Product" : "Create New Product"}
                </h1>
                <p className="text-sm text-gray-500 mt-1">Add and manage your product catalog details.</p>
            </div>

            {/* Form Body */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">

                {/* Basic Info */}
                <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-4">Product Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={productFormData.name}
                                onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="e.g. Summer Collection 2024"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Launch Date</label>
                            <input
                                type="date"
                                value={productFormData.launchDate}
                                onChange={(e) => setProductFormData({ ...productFormData, launchDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">End Date</label>
                            <input
                                type="date"
                                value={productFormData.endDate}
                                onChange={(e) => setProductFormData({ ...productFormData, endDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                            <textarea
                                rows={3}
                                value={productFormData.description}
                                onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Brief product description"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                            <textarea
                                rows={3}
                                value={productFormData.notes}
                                onChange={(e) => setProductFormData({ ...productFormData, notes: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Internal notes for your team"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                            <select
                                value={productFormData.status}
                                onChange={(e) => setProductFormData({ ...productFormData, status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="Active">Active</option>
                                <option value="Archived">Archived</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Tags (comma separated)</label>
                            <input
                                type="text"
                                value={productTagsInput}
                                onChange={(e) => {
                                    setProductTagsInput(e.target.value);
                                    setProductFormData({ ...productFormData, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="tag1, tag2, tag3"
                            />
                        </div>
                    </div>
                </div>

                {/* Media Upload */}
                <div className="pt-6 border-t border-gray-100">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">Product Image</h3>
                    <div
                        className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ borderColor: THEME_PRIMARY_RING }}
                        onClick={() => imageInputRef.current?.click()}
                    >
                        {imagePreview ? (
                            <div className="relative inline-block">
                                <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded-lg object-contain" />
                                <button
                                    onClick={(e) => { e.stopPropagation(); setImagePreview(""); setProductFormData({ ...productFormData, image: "" }); }}
                                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                                >×</button>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">Click to upload an image</p>
                        )}
                        <input type="file" ref={imageInputRef} accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </div>
                </div>

                {/* Linked Events & Assets (Side by Side Grid with Checkboxes) */}
                <div className="pt-6 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-8">

                    {/* Linked Events */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-1">
                            <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            Link events (optional)
                        </label>
                        <p className="text-xs text-gray-500 mb-3">Link related events to this campaign.</p>


                        {/* Display Selected Events as Chips */}
                        {(productFormData.events.length > 0 || selectedEventIds.length > 0) && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {[
                                    ...productFormData.events,
                                    ...availableEvents.filter(
                                        (evt: any) =>
                                            selectedEventIds.includes(evt.id) &&
                                            !productFormData.events.some(
                                                (added: any) => added.id === evt.id
                                            )
                                    ),
                                ].map((evt: any) => (
                                    <span
                                        key={evt.id}
                                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-xs font-medium"
                                    >
                                        <span className="truncate max-w-[150px]">{evt.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (selectedEventIds.includes(evt.id)) {
                                                    setSelectedEventIds(prev =>
                                                        prev.filter(x => x !== evt.id)
                                                    );
                                                }
                                                removeEvent(evt.id);
                                            }}
                                            className="text-gray-400 hover:text-red-500 shrink-0"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-2 relative event-dropdown-container">
                            <div className="relative flex-1">
                                <button
                                    type="button"
                                    onClick={() => setShowEventDropdown(!showEventDropdown)}
                                    className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-left text-gray-500 hover:border-gray-400 transition-colors shadow-sm"
                                >
                                    <span className="truncate">Choose related events</span>
                                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {/* Dropdown Menu with Checkboxes */}
                                {showEventDropdown && (
                                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                        <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                                            <input
                                                type="text"
                                                placeholder="Search events..."
                                                value={eventSearch}
                                                onChange={(e) => setEventSearch(e.target.value)}
                                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto p-1">
                                            {filteredEvents.length === 0 ? (
                                                <p className="p-3 text-sm text-gray-500 text-center">No events found.</p>
                                            ) : (
                                                filteredEvents.map((evt: any) => (
                                                    <label key={evt.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedEventIds.includes(evt.id)}
                                                            onChange={() => toggleSelectEvent(evt.id)}
                                                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                                        />
                                                        <span className="text-gray-700">{evt.name}</span>
                                                    </label>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Red Plus Button (Bulk Add) */}
                            <button
                                type="button"
                                onClick={handleBulkAddEvents}
                                disabled={selectedEventIds.length === 0}
                                className="px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 transition-colors shadow-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Linked Assets */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-1">
                            <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            Link assets (optional)
                        </label>
                        <p className="text-xs text-gray-500 mb-3">Select product assets to include in this campaign.</p>


                        {/* Display Selected Assets as Chips */}
                        {(productFormData.assets.length > 0 || selectedAssetIds.length > 0) && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {[
                                    ...productFormData.assets,
                                    ...availableAssets.filter(
                                        (ast: any) =>
                                            selectedAssetIds.includes(ast.id) &&
                                            !productFormData.assets.some(
                                                (added: any) => added.id === ast.id
                                            )
                                    ),
                                ].map((ast: any) => (
                                    <span
                                        key={ast.id}
                                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-xs font-medium"
                                    >
                                        <span className="truncate max-w-[150px]">{ast.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (selectedAssetIds.includes(ast.id)) {
                                                    setSelectedAssetIds(prev =>
                                                        prev.filter(x => x !== ast.id)
                                                    );
                                                }
                                                removeAsset(ast.id);
                                            }}
                                            className="text-gray-400 hover:text-red-500 shrink-0"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-2 relative asset-dropdown-container">
                            <div className="relative flex-1">
                                <button
                                    type="button"
                                    onClick={() => setShowAssetDropdown(!showAssetDropdown)}
                                    className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-left text-gray-500 hover:border-gray-400 transition-colors shadow-sm"
                                >
                                    <span className="truncate">Select product assets</span>
                                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {/* Dropdown Menu with Checkboxes */}
                                {showAssetDropdown && (
                                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                        <div className="p-2 border-b border-gray-100 bg-gray-50/50">
                                            <input
                                                type="text"
                                                placeholder="Search assets..."
                                                value={assetSearch}
                                                onChange={(e) => setAssetSearch(e.target.value)}
                                                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto p-1">
                                            {filteredAssets.length === 0 ? (
                                                <p className="p-3 text-sm text-gray-500 text-center">No assets found.</p>
                                            ) : (
                                                filteredAssets.map((ast: any) => (
                                                    <label key={ast.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedAssetIds.includes(ast.id)}
                                                            onChange={() => toggleSelectAsset(ast.id)}
                                                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                                        />
                                                        <span className="text-gray-700 truncate">{ast.name}</span>
                                                    </label>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Red Plus Button (Bulk Add) */}
                            <button
                                type="button"
                                onClick={handleBulkAddAssets}
                                disabled={selectedAssetIds.length === 0}
                                className="px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 transition-colors shadow-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                        </div>
                    </div>

                </div>

            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
                <button
                    onClick={() => navigate(returnPath)}
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2.5 text-white rounded-xl disabled:opacity-70 font-medium text-sm shadow-sm flex items-center justify-center transition-opacity"
                    style={{ backgroundColor: THEME_PRIMARY }}
                >
                    {saving ? "Saving..." : isEditMode ? "Update Product" : "Create Product"}
                </button>
            </div>

        </div>
    );
};

export default ProductFormPage;